import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useAuthStore } from '@/stores';
import { authService } from '@/services/auth/auth-service';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

/**
 * Resolve the post-login target from the `?redirect=` param.
 *
 * Parsed against the current origin rather than string-matched: the redirect now
 * carries a search string, and it is applied via navigate({href}), which skips
 * route matching. A `startsWith('/')` check is not enough there — the URL parser
 * folds `\` into `/`, so `/\evil.com` reads as protocol-relative `//evil.com`
 * and would leave the origin. Anything not same-origin falls back to /inventory.
 */
const toSafeRedirect = (raw: string | null): string => {
  if (!raw) return '/inventory';
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return '/inventory';
    return `${url.pathname}${url.search}`;
  } catch {
    return '/inventory';
  }
};

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, setUser, setAuthenticated, clearAuth } = useAuthStore();
  const [isVerified, setIsVerified] = useState(false);
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (!requireAuth) {
      if (isAuthenticated && location.pathname === '/login') {
        const searchParams = new URLSearchParams(location.searchStr);
        // href, not to: the redirect carries a search string, which `to` would
        // drop. Returning the user to a bare path is what made a session blip on
        // /inventory?tab=counterparty-reports come back as /inventory, letting
        // the view bootstrap write ?view_id=<default> over the tab (LR-536).
        navigate({ href: toSafeRedirect(searchParams.get('redirect')) });
      }
      return;
    }

    // Already verified this mount — skip
    if (verifiedRef.current) return;

    // Verify auth with server (cookie-based)
    authService.getMe()
      .then((userInfo) => {
        const restoredUser = {
          id: userInfo.email,
          email: userInfo.email,
          name: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || userInfo.email,
          role: userInfo.role ?? '',
          permissions: userInfo.permissions ?? [],
          lastLoginAt: new Date(),
          onboardingSeenVersion: userInfo.onboarding_seen_version,
        };
        setUser(restoredUser);
        setAuthenticated(true);
        verifiedRef.current = true;
        setIsVerified(true);
      })
      .catch(() => {
        clearAuth();
        verifiedRef.current = true;
        setIsVerified(true);
        navigate({
          to: '/login',
          // Carry the search string too, not just the path: dropping it sends
          // the user back to a bare route after login (LR-536).
          search: { redirect: `${location.pathname}${location.searchStr}` }
        });
      });
  }, [requireAuth, isAuthenticated, navigate, location.pathname, location.searchStr, setUser, setAuthenticated, clearAuth]);

  if (requireAuth && !isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    return null;
  }

  return <>{children}</>;
};
